import React from 'react';
import { ROOMS } from '../Constants/rooms';
import { buildMonthMatrix, todayYmd, WEEKDAY_LABELS } from '../Util/dateUtil';
import '../CSS/style/Availability.css';

/**
 * 월 단위 객실 예약현황 그리드 (표시 전용).
 * 메인 페이지(읽기)와 관리자 페이지(클릭 가능)가 함께 사용합니다.
 *
 * @param {number} year
 * @param {number} month            1~12
 * @param {Object<string,string[]>} bookedByDate  { 'YYYY-MM-DD': ['단풍', ...] }
 * @param {string}  [selectedDate]  선택된 날짜(YYYY-MM-DD)
 * @param {Function} [onDayClick]   (ymd) => void. 없으면 셀이 클릭 불가로 렌더됩니다.
 * @param {boolean} [dimPast=true]  지난 날짜를 흐리게 처리할지
 */
const CalendarGrid = ({ year, month, bookedByDate = {}, selectedDate, onDayClick, dimPast = true }) => {
    const weeks = buildMonthMatrix(year, month);
    const today = todayYmd();
    const total = ROOMS.length;

    return (
        <div className="cal-grid">
            <div className="cal-head">
                {WEEKDAY_LABELS.map((label, i) => (
                    <div key={label} className={`cal-head-cell cal-wd-${i}`}>{label}</div>
                ))}
            </div>

            <div className="cal-body">
                {weeks.map((week, wi) => (
                    <div key={wi} className="cal-week">
                        {week.map(({ ymd, date, inMonth, weekday }) => {
                            const booked = bookedByDate[ymd] || [];
                            const availableCount = total - booked.length;
                            const isPast = dimPast && ymd < today;
                            const classes = [
                                'cal-cell',
                                `cal-wd-${weekday}`,
                                inMonth ? '' : 'cal-cell--outside',
                                isPast ? 'cal-cell--past' : '',
                                ymd === today ? 'cal-cell--today' : '',
                                ymd === selectedDate ? 'cal-cell--selected' : '',
                                inMonth && availableCount === 0 ? 'cal-cell--full' : '',
                                onDayClick ? 'cal-cell--clickable' : '',
                            ].filter(Boolean).join(' ');

                            const cellContent = (
                                <>
                                    <span className="cal-date">{date.getDate()}</span>
                                    {inMonth && (
                                        <>
                                            <span className="cal-dots">
                                                {ROOMS.map(({ key, color }) => {
                                                    const isBooked = booked.includes(key);
                                                    return (
                                                        <i
                                                            key={key}
                                                            className={`cal-dot${isBooked ? ' cal-dot--booked' : ''}`}
                                                            style={isBooked ? undefined : { borderColor: color }}
                                                            title={`${key} ${isBooked ? '예약완료' : '예약가능'}`}
                                                        />
                                                    );
                                                })}
                                            </span>
                                            <span className={`cal-count${availableCount === 0 ? ' cal-count--full' : ''}`}>
                                                {availableCount === 0 ? '마감' : `${availableCount}/${total}`}
                                            </span>
                                        </>
                                    )}
                                </>
                            );

                            return onDayClick && inMonth ? (
                                <button
                                    key={ymd}
                                    type="button"
                                    className={classes}
                                    onClick={() => onDayClick(ymd)}
                                    aria-label={`${date.getMonth() + 1}월 ${date.getDate()}일, 예약가능 ${availableCount}실`}
                                >
                                    {cellContent}
                                </button>
                            ) : (
                                <div key={ymd} className={classes}>{cellContent}</div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CalendarGrid;